import { useEffect } from 'react';
import { useNavigate } from 'react-router';

import Provision from '../../../../../api/provision';
import Table from '../../../../../components/Table';
import useApiQuery from '../../../../../hooks/useApiQuery';
import rbacCheck from '../../../../../rbac/rbacCheck';

import { useUserData } from '@/redux/selectors';
import type { ErrorType } from '@/types';
import type { OsProject, Product } from '@/types/productTypes';
import type { TableField, TableRowData } from '@/types/tableTypes';

export interface OsProjectsProps {
  setActiveResources: (hasActive: boolean) => void;
  product: Product;
}

const OsProjects = ({ setActiveResources, product }: OsProjectsProps): React.JSX.Element => {
  const navigate = useNavigate();
  const userData = useUserData();
  const {
    data: osProjects = [],
    error: fetchError,
    makeApiCall: fetchOsProjects,
  } = useApiQuery<OsProject[]>(Provision.fetchOsProjectsV1);

  useEffect(() => {
    if (product.details.juiceGroup) {
      fetchOsProjects(product.details.juiceGroup);
    }
  }, [product.details.juiceGroup, fetchOsProjects]);

  useEffect(() => {
    if (osProjects.length) {
      setActiveResources(
        osProjects.some(osProject =>
          ['active', 'deploying', 'deleting', 'deletionFailed'].includes(osProject.status)
        )
      );
    }
  }, [osProjects, setActiveResources]);

  const canUserNavigateToDetails = (osProjectName: string): boolean => {
    const osProject = osProjects.find(project => project.name === osProjectName);
    if (!osProject) return false;

    const canPatch = rbacCheck(userData.roles, '/osprojects:PATCH', {
      productId: product._id,
      osProjectType: osProject.type,
      userProducts: userData.productIds,
    }) as boolean;

    const canDelete = rbacCheck(userData.roles, '/osprojects:DELETE', {
      productId: product._id,
      osProjectType: osProject.type,
      userProducts: userData.productIds,
    }) as boolean;

    if (osProject.status === 'deletionFailed') {
      return canPatch && canDelete;
    }

    return osProject.status === 'active' && canDelete;
  };

  const fields: TableField[] = [
    { id: 'name', title: { id: 'name' } },
    { id: 'cloudId', title: { id: 'cloud' } },
    { id: 'expirationDate', title: { id: 'expirationDate' } },
    { id: 'status', title: { id: 'status' } },
  ];

  const rows = osProjects.map(osProject => ({
    key: `${osProject.name}+${osProject.cloudId}`,
    onRowClick: canUserNavigateToDetails(osProject.name)
      ? () =>
          navigate(
            `/products/${product._id}/osprojects/${product.details.juiceGroup}/${osProject.cloudId}/${osProject.name}`
          )
      : undefined,
    data: [
      { field: 'name', value: osProject.name },
      { field: 'cloudId', value: osProject.cloudId },
      {
        field: 'expirationDate',
        value: osProject.expirationDate ? osProject.expirationDate.split('T')[0] : '-',
      },
      { field: 'status', value: osProject.status },
    ],
  })) as TableRowData[];

  return (
    <Table
      fields={fields}
      rows={rows}
      error={fetchError as ErrorType}
    />
  );
};

export default OsProjects;
